// Product tour — interactive spotlight walkthrough shown to:
//   • Account creators the first time they land on the app after finishing
//     onboarding (route redirect adds ?tour=1)
//   • Invited teammates the first time they log in (auto-starts when
//     user.product_tour_completed_at is null)
//   • Anyone who clicks "Take a tour" from the user dropdown
//
// The tour highlights each sidebar module in turn with a small tooltip card,
// finishing by navigating to Settings → Integrations so the user can hook up
// Gmail immediately.
//
// State tracking lives on the user document (`product_tour_completed_at`),
// updated via POST /api/auth/me/complete-tour and reset via
// POST /api/auth/me/restart-tour.

import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, X, Mail } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Steps: [{ target: dataTestId, path, title, body, cta?, tone? }]
// - target: element to spotlight. If null, tooltip renders centered on screen.
// - path: route to navigate to when the step activates (unless already there).
// - tone: "final" bumps the visual for the last step.
const STEPS = [
  {
    target: null,
    path: "/dashboard",
    title: "Welcome to Pragma",
    body: "Let's take a quick 60-second tour to show you where everything lives.",
    cta: "Show me around",
  },
  {
    target: "nav-dashboard",
    path: "/dashboard",
    title: "Dashboard",
    body: "Your daily home base. See your open deals, this month's revenue, and moves due today at a glance.",
  },
  {
    target: "nav-moves",
    path: "/moves",
    title: "Moves",
    body: "Every follow-up you need to make today — calls, emails, meetings. If it's not a move, it doesn't happen.",
  },
  {
    target: "nav-leads",
    path: "/leads",
    title: "Lead Inbox",
    body: "New leads land here first. Qualify and convert them into contacts and deals — or mark them lost with a reason.",
  },
  {
    target: "nav-deals",
    path: "/deals",
    title: "Acquisition Pipeline",
    body: "Your kanban of new-business deals. Drag cards across stages, log activity, and watch weighted revenue build up.",
  },
  {
    target: "nav-clients",
    path: "/clients",
    title: "Clients",
    body: "Every won deal becomes a client. Track deliverables, renewals, and expansion opportunities from one place.",
  },
  {
    target: "nav-growth",
    path: "/growth",
    title: "Growth Pipeline",
    body: "Renewals and upsells live here. Pragma auto-creates a renewal deal when a recurring acquisition deal is won.",
  },
  {
    target: "nav-insights",
    path: "/insights",
    title: "Insights",
    body: "Rolling 12-month forecast, team attainment, funnel conversion, activity heatmap — the numbers that matter.",
  },
  {
    target: "integration-gmail-connect",
    path: "/settings?tab=integrations",
    title: "Connect Gmail last",
    body: "Sync your inbox to log every email against the right deal and see when clients open your messages.",
    cta: "Connect Gmail",
    tone: "final",
  },
];

const PAD = 8;
const MASK_RADIUS = 12;
const CARD_OFFSET = 16;
const CARD_WIDTH = 340;
const CARD_MAX_HEIGHT = 220;

export default function ProductTour({ open, onClose }) {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);

  // Reset step when the tour opens
  useEffect(() => { if (open) setStep(0); }, [open]);

  const currentStep = STEPS[step];

  // When the step changes, navigate to the required page (if we aren't already there).
  useEffect(() => {
    if (!open || !currentStep?.path) return;
    const targetPath = currentStep.path.split("?")[0];
    if (location.pathname !== targetPath) {
      navigate(currentStep.path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, open]);

  // Poll for the target element after navigation completes.
  useLayoutEffect(() => {
    if (!open) return;
    if (!currentStep?.target) { setRect(null); return; }

    let cancelled = false;
    let attempts = 0;
    const tryLocate = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-testid="${currentStep.target}"]`);
      if (el) {
        const box = el.getBoundingClientRect();
        // Scroll into view if off-screen
        if (box.top < 60 || box.bottom > window.innerHeight - 40) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setTimeout(() => {
            if (cancelled) return;
            setRect(el.getBoundingClientRect());
          }, 260);
        } else {
          setRect(box);
        }
      } else if (attempts++ < 30) {
        setTimeout(tryLocate, 80);
      }
    };
    tryLocate();
    const onResize = () => {
      const el = document.querySelector(`[data-testid="${currentStep.target}"]`);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [step, open, location.pathname, currentStep?.target]);

  const finish = async () => {
    try { await api.post("/auth/me/complete-tour"); if (refresh) await refresh(); } catch { /* non-fatal */ }
    onClose?.();
  };

  const next = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
    else finish();
  };
  const back = () => step > 0 && setStep(step - 1);

  if (!open || !user) return null;

  const isLast = step === STEPS.length - 1;

  // Compute where to place the tooltip relative to the target rect.
  const cardPos = computeCardPosition(rect);

  return ReactDOM.createPortal(
    <div data-testid="product-tour" style={{ position: "fixed", inset: 0, zIndex: 500 }}>
      {/* SVG dimmer with spotlight cutout */}
      <svg
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "auto" }}
        onClick={next}
      >
        <defs>
          <mask id="pragmaTourMask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - PAD}
                y={rect.top - PAD}
                width={rect.width + PAD * 2}
                height={rect.height + PAD * 2}
                rx={MASK_RADIUS}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(15, 23, 42, 0.62)"
          mask="url(#pragmaTourMask)"
        />
      </svg>

      {/* Spotlight ring around the highlighted element */}
      {rect && (
        <div
          style={{
            position: "absolute",
            left: rect.left - PAD, top: rect.top - PAD,
            width: rect.width + PAD * 2, height: rect.height + PAD * 2,
            borderRadius: MASK_RADIUS,
            boxShadow: "0 0 0 2px var(--pg-accent, #5B4FE8), 0 0 0 6px rgba(91,79,232,0.25)",
            pointerEvents: "none",
            animation: "pragmaTourPulse 1.6s ease-out infinite",
          }}
        />
      )}

      {/* Tooltip card */}
      <div
        role="dialog" aria-modal="true"
        data-testid="product-tour-card"
        style={{
          position: "absolute", ...cardPos,
          width: CARD_WIDTH,
          background: "var(--pg-surface, #fff)",
          border: "1px solid var(--pg-border)",
          borderRadius: 12,
          boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
          padding: 18,
          animation: "pragmaTourCardIn 0.22s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span className="mono text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--pg-accent, #5B4FE8)" }}>
            {step + 1} of {STEPS.length}
          </span>
          <button
            onClick={finish}
            aria-label="Skip tour"
            data-testid="product-tour-skip"
            style={{
              width: 22, height: 22, border: 0, background: "transparent",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "var(--pg-text-mute)", cursor: "pointer", borderRadius: 4,
            }}
          ><X size={14} /></button>
        </div>

        <h3 style={{ fontSize: 16, fontWeight: 600, color: "var(--pg-text)", margin: 0, lineHeight: 1.3 }}
            data-testid="product-tour-title">
          {currentStep.title}
        </h3>
        <p style={{
          fontSize: 13, color: "var(--pg-text-mute)", lineHeight: 1.55, margin: "8px 0 16px",
        }} data-testid="product-tour-body">
          {currentStep.body}
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          {step > 0 ? (
            <button
              onClick={back}
              className="mono text-[11px] uppercase tracking-wider"
              style={{ color: "var(--pg-text-mute)", background: "none", border: 0, cursor: "pointer", padding: 0 }}
              data-testid="product-tour-back"
            >Back</button>
          ) : <span />}
          <div style={{ display: "flex", gap: 8 }}>
            {!isLast && (
              <button
                onClick={finish}
                className="mono text-[11px] uppercase tracking-wider"
                style={{ color: "var(--pg-text-mute)", background: "none", border: 0, cursor: "pointer", padding: "6px 10px" }}
                data-testid="product-tour-finish-early"
              >Skip tour</button>
            )}
            <button
              onClick={next}
              className="pg-btn pg-btn-primary"
              data-testid="product-tour-next"
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {isLast
                ? <><Mail size={13} /> Got it, thanks</>
                : <>{currentStep.cta || "Next"} <ArrowRight size={13} /></>}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Place the card next to the highlighted target, or centered if there's no target.
function computeCardPosition(rect) {
  const vw = window.innerWidth, vh = window.innerHeight;
  if (!rect) {
    return {
      left: Math.max(16, (vw - CARD_WIDTH) / 2),
      top: Math.max(16, (vh - CARD_MAX_HEIGHT) / 2),
    };
  }
  // Prefer to the right of the target (sidebar case). Fall back to below.
  const spaceRight = vw - rect.right - CARD_OFFSET;
  const spaceBelow = vh - rect.bottom - CARD_OFFSET;
  if (spaceRight >= CARD_WIDTH + 20) {
    return {
      left: rect.right + CARD_OFFSET,
      top: Math.max(16, Math.min(vh - CARD_MAX_HEIGHT - 16, rect.top - 8)),
    };
  }
  if (spaceBelow >= CARD_MAX_HEIGHT + 20) {
    return {
      top: rect.bottom + CARD_OFFSET,
      left: Math.max(16, Math.min(vw - CARD_WIDTH - 16, rect.left)),
    };
  }
  // Fallback: above target
  return {
    bottom: vh - rect.top + CARD_OFFSET,
    left: Math.max(16, Math.min(vw - CARD_WIDTH - 16, rect.left)),
  };
}

// Hook: call inside Layout so the tour auto-starts when the user hasn't seen it,
// OR when arriving with `?tour=1`, OR when triggered manually.
export function useProductTourController() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [manualStart, setManualStart] = useState(0);

  // Auto-start once per user session
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(location.search);
    const forced = params.get("tour") === "1";
    const firstTime = !user.product_tour_completed_at;
    if (forced || firstTime) {
      setOpen(true);
      if (forced) {
        // clean up the URL param
        params.delete("tour");
        navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, manualStart]);

  const start = () => {
    // Reset server-side so future logins don't re-fire, but keep it open for now.
    api.post("/auth/me/restart-tour").catch(() => { /* non-fatal */ });
    setManualStart((n) => n + 1);
    setOpen(true);
  };

  return useMemo(() => ({ open, start, close: () => setOpen(false) }), [open]);
}
